import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { RevealHeading } from "./Motion";
import { Squiggle, ScribbleCircle, Blob, BnGlyph, Sparkle, DotGrid } from "./Decorations";
import { StaticWaveform } from "./Waveform";
import { Clipboard, Cpu, Code2, Headphones } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
    {
        num: "01",
        title: "Paste your article",
        sub: "Drop Bengali text into the dashboard, or auto-sync via API.",
        color: "#FF5B3E",
        icon: Clipboard,
    },
    {
        num: "02",
        title: "AI converts to Bengali audio",
        sub: "30 seconds. Natural voices. No robotic tone.",
        color: "#3D5BFF",
        icon: Cpu,
    },
    {
        num: "03",
        title: "Copy one line of embed code",
        sub: "<script src=\"audo.dev/embed/...\"></script>",
        color: "#2EE8B5",
        icon: Code2,
    },
    {
        num: "04",
        title: "Readers press play — and stay 3× longer",
        sub: "Listening is the new reading. Engagement just compounds.",
        color: "#FFD23F",
        icon: Headphones,
    },
];

const StepVisual = ({ step, index }) => {
    const Icon = step.icon;
    return (
        <div className="relative w-full h-[440px] lg:h-[560px] bg-white rounded-[40px] border border-cream-300 shadow-soft-lg overflow-hidden flex items-center justify-center">
            {/* Step indicator */}
            <span className="absolute top-7 left-7 text-[12px] uppercase tracking-[0.22em] font-semibold text-ink-muted">
                Step {step.num}
            </span>
            <div className="absolute top-7 right-7 w-3 h-3 rounded-full" style={{ background: step.color }}/>

            {/* Decorations behind */}
            <ScribbleCircle color={step.color} className="absolute -bottom-10 -right-10 w-48 h-48 opacity-20"/>
            <DotGrid color="#0A0A0A" rows={6} cols={6} className="absolute top-12 left-1/2 -translate-x-1/2 w-32 h-32 opacity-10"/>

            {/* Step-specific visual */}
            {index === 0 && (
                <div className="relative bg-cream-100 rounded-3xl p-6 w-[80%] max-w-md border border-cream-300 shadow-soft">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-2.5 h-2.5 rounded-full bg-coral"/>
                        <div className="w-2.5 h-2.5 rounded-full bg-sunshine"/>
                        <div className="w-2.5 h-2.5 rounded-full bg-mint"/>
                    </div>
                    <p className="bn text-[15px] leading-[1.55] text-ink">
                        ঢাকার রাজপথে আজ সকাল থেকে বৃষ্টি। যানজট তীব্র। নাগরিকরা বিকল্প পথ খুঁজছেন...
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 bg-coral text-white text-[12px] font-semibold px-3.5 py-1.5 rounded-full">
                        <Clipboard size={12}/> Pasted
                    </div>
                </div>
            )}
            {index === 1 && (
                <div className="relative w-[80%] max-w-md text-center">
                    <div className="inline-flex w-24 h-24 rounded-full items-center justify-center mb-5" style={{ background: step.color + "22" }}>
                        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: step.color }}>
                            <Cpu className="text-white" size={28}/>
                        </div>
                    </div>
                    <p className="font-mono text-[12px] text-ink-muted">[████████████░░░] 78%</p>
                    <p className="mt-2 text-[14px] text-ink">Generating Bengali audio...</p>
                    <p className="text-[11px] text-ink-muted mt-1">~12s remaining</p>
                </div>
            )}
            {index === 2 && (
                <div className="relative bg-ink-pill rounded-2xl p-5 w-[85%] max-w-md font-mono text-[13px] leading-[1.6] shadow-soft-lg">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5B5B]"/>
                        <span className="w-2.5 h-2.5 rounded-full bg-sunshine"/>
                        <span className="w-2.5 h-2.5 rounded-full bg-mint"/>
                        <span className="ml-auto text-[10px] text-cream-100/50">embed.html</span>
                    </div>
                    <div className="text-cream-100">
                        <span className="text-cream-100/40">{"<!-- Drop this anywhere -->"}</span><br/>
                        <span className="text-coral">{"<script"}</span> <span className="text-mint">src</span>=<span className="text-sunshine">"https://audo.dev/embed/v1.js"</span><br/>
                        &nbsp;&nbsp;<span className="text-mint">data-article</span>=<span className="text-sunshine">"a1b2c3"</span><span className="text-coral">{"></script>"}</span>
                    </div>
                </div>
            )}
            {index === 3 && (
                <div className="relative w-[80%] max-w-md">
                    <div className="bg-cream-100 rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-full bg-coral text-white flex items-center justify-center">
                                <Headphones size={20}/>
                            </div>
                            <div>
                                <p className="text-[12px] text-ink-muted">Reader on Prothom Alo</p>
                                <p className="text-[14px] font-semibold">Listening for 8m 42s</p>
                            </div>
                        </div>
                        <StaticWaveform bars={32} color="#FF5B3E" height={48}/>
                        <div className="mt-4 flex items-center justify-between text-[11px] font-mono text-ink-muted">
                            <span>3:04</span>
                            <span>8:42</span>
                        </div>
                    </div>
                    <Sparkle color={step.color} className="absolute -top-4 -right-4 w-10 h-10 animate-float-fast"/>
                </div>
            )}
        </div>
    );
};

export const HowItWorks = () => {
    const sectionRef = useRef(null);
    const trackRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        if (window.innerWidth < 1024) return;

        const ctx = gsap.context(() => {
            const panels = trackRef.current.querySelectorAll("[data-panel]");
            panels.forEach((p, i) => {
                if (i === 0) return;
                gsap.set(p, { opacity: 0, y: 80 });
            });
            const hold = 1.5; // extra scroll hold per panel so text is readable
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    pin: true,
                    start: "top top",
                    end: () => `+=${panels.length * 1200}`,
                    scrub: 0.8,
                    anticipatePin: 1,
                },
            });
            panels.forEach((p, i) => {
                if (i === 0) return;
                const base = (i - 1) * (1 + hold);
                tl.to(panels[i - 1], { opacity: 0, y: -80, duration: 1 }, base + hold)
                  .to(p, { opacity: 1, y: 0, duration: 1 }, base + hold);
            });
        }, sectionRef);
        return () => ctx.revert();
    }, []);

    return (
        <section id="how" ref={sectionRef} className="relative py-14 lg:py-16 overflow-hidden bg-cream-100">
            <Blob color="#FFD23F" className="absolute top-10 right-[-80px] w-72 h-72 opacity-30"/>
            <BnGlyph glyph="ক" className="absolute right-[10%] bottom-10 text-[200px] lg:text-[300px] leading-none font-bold opacity-[0.05] select-none" color="#0A0A0A"/>

            <div className="relative mx-auto max-w-[1240px] px-5 sm:px-8">
                <div className="max-w-[920px] mb-6 lg:mb-8">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-3">How it works</p>
                    <RevealHeading as="h2" className="h-display text-[36px] sm:text-[52px] lg:text-[68px] text-ink">
                        From article to audio in four steps.
                    </RevealHeading>
                </div>

                <div ref={trackRef} className="relative">
                    {/* Pinned (desktop) - panels stacked, animated via GSAP */}
                    <div className="hidden lg:grid grid-cols-2 gap-8 lg:gap-12 items-center min-h-[420px] relative">
                        {STEPS.map((step, i) => (
                            <React.Fragment key={i}>
                                <div data-panel className={`${i === 0 ? "" : "absolute inset-0 grid grid-cols-2 gap-12 items-center"}`}>
                                    <div>
                                        <span className="font-display italic-display text-[80px] leading-none" style={{ color: step.color }}>{step.num}</span>
                                        <h3 className="mt-2 font-display text-[32px] lg:text-[40px] font-semibold tracking-tight text-ink leading-[1.05]">
                                            {step.title}
                                        </h3>
                                        <p className="mt-3 text-[16px] text-ink-muted max-w-md leading-relaxed">{step.sub}</p>
                                    </div>
                                    <StepVisual step={step} index={i}/>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Mobile / tablet - vertical stack */}
                    <div className="lg:hidden space-y-12">
                        {STEPS.map((step, i) => (
                            <div key={i} className="grid grid-cols-1 gap-6">
                                <div>
                                    <span className="font-display italic-display text-[80px] leading-none" style={{ color: step.color }}>{step.num}</span>
                                    <h3 className="mt-3 font-display text-[32px] sm:text-[40px] font-semibold tracking-tight text-ink leading-[1.05]">
                                        {step.title}
                                    </h3>
                                    <p className="mt-3 text-[16px] text-ink-muted leading-relaxed">{step.sub}</p>
                                </div>
                                <StepVisual step={step} index={i}/>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
